/**
 * Contenu d'une version de gamme : points théoriques et tableau des lignes de
 * chaque palier. Partagé par la carte de la fiche machine et l'écran « Gammes ».
 *
 * Les colonnes de prix n'apparaissent que si l'API a RENDU des prix : les clés
 * sont absentes, pas nulles, pour qui n'y a pas droit (`pricing:read:tenant`).
 *
 * LOT P2 (registre B26) : une version publiée avec les conditions d'achat porte, en plus du prix du TARIF du fabricant,
 * un prix de VENTE (ce que voit le client) et un coût d'achat RÉEL, calculés à sa publication puis figés. Quand la
 * version en porte, les sous-totaux se lisent au prix de VENTE et chaque palier dit sa marge ; sinon (version publiée
 * avant le lot P2, ou conditions non saisies) tout reste au prix du tarif, étiqueté comme tel.
 *
 * Une quantité inconnue s'affiche « — » avec la note de la ligne : la maquette
 * affichait « 1 » pour une valeur que l'export constructeur ne donnait pas.
 */
import type { GammeVersion } from '../api/client';
import {
  GENRE_LIGNE,
  decrireNiveau,
  phraseTempsMo,
  niveauxAvecEquivalents,
  niveauxAvecPrix,
  prix,
  quantite,
  resumerEcheances,
} from '../domaine/gammes';
import {
  aDesPrixDAchat,
  aDesPrixDeVente,
  niveauxAvecPrixCalcules,
  type GammeLigneVue,
  type GammeNiveauVue,
} from '../domaine/gammes-prix-calcules';
import {
  type MargePalier,
  type SousTotalPalier,
  margePalier,
  montantMarge,
  montantSousTotal,
  recapitulatifSousTotaux,
  reserveMarge,
  reserveSousTotal,
  sousTotalPalier,
  sousTotauxDeLaVersion,
  totalLigneCentimes,
} from '../domaine/gammes-sous-totaux';

export function ContenuGamme({ version }: { version: GammeVersion }) {
  const niveaux = niveauxAvecPrixCalcules(version);
  const avecPrix = niveauxAvecPrix(niveaux);
  const avecVente = aDesPrixDeVente(niveaux);
  const avecAchat = aDesPrixDAchat(niveaux);
  const avecEquivalents = niveauxAvecEquivalents(niveaux);
  const source = avecVente ? 'VENTE' : 'TARIF';
  const sousTotaux = sousTotauxDeLaVersion(niveaux, source);
  const { heures, mois } = version.echeances;

  if (niveaux.length === 0) {
    return <div className="aide">Cette version ne contient encore aucun palier.</div>;
  }

  return (
    <>
      {(heures.length > 0 || mois.length > 0) && (
        <details className="echeances-theoriques">
          <summary>Échéances théoriques</summary>
          {heures.length > 0 && (
            <p className="aide">
              <strong>Au compteur :</strong> {resumerEcheances(heures, 'h')}
            </p>
          )}
          {mois.length > 0 && (
            <p className="aide">
              <strong>Au calendrier :</strong> {resumerEcheances(mois, 'mois')}
            </p>
          )}
          <p className="aide">
            À un même point, un seul palier s’applique : le plus englobant. Ce sont des points
            théoriques ; le calcul de la prochaine échéance d’une machine viendra avec ses compteurs.
          </p>
        </details>
      )}

      {sousTotaux && (
        <p className="recap-sous-totaux">
          <strong>
            {avecVente ? 'Sous-total au prix de vente HT par palier :' : 'Sous-total au prix du tarif HT par palier :'}
          </strong>{' '}
          {recapitulatifSousTotaux(sousTotaux)}
        </p>
      )}

      {niveaux.map((niveau) => (
        <Palier
          key={niveau.levelKey}
          niveau={niveau}
          avecPrix={avecPrix}
          avecVente={avecVente}
          avecAchat={avecAchat}
          avecEquivalents={avecEquivalents}
          sousTotal={sousTotalPalier(niveau, source)}
          marge={avecVente && avecAchat ? margePalier(niveau) : null}
        />
      ))}
    </>
  );
}

function Palier({
  niveau,
  avecPrix,
  avecVente,
  avecAchat,
  avecEquivalents,
  sousTotal,
  marge,
}: {
  niveau: GammeNiveauVue;
  avecPrix: boolean;
  avecVente: boolean;
  avecAchat: boolean;
  avecEquivalents: boolean;
  sousTotal: SousTotalPalier | null;
  marge: MargePalier | null;
}) {
  // Colonnes : désignation, référence, quantité, [prix du tarif, (prix de vente,) total], [référence adaptable, prix adaptable], [coût d'achat].
  const nbColonnesPrix = avecPrix ? (avecVente ? 3 : 2) : 0;
  const nbColonnes = 3 + nbColonnesPrix + (avecEquivalents ? 1 : 0) + (avecPrix && avecEquivalents ? 1 : 0) + (avecPrix && avecAchat ? 1 : 0);
  const reserve = sousTotal ? reserveSousTotal(sousTotal) : null;
  const reserveDeMarge = marge ? reserveMarge(marge) : null;
  // La colonne du total est la dernière de celles de gauche : le libellé du sous-total couvre tout ce qui la précède.
  const decalageLibelle = avecVente ? 5 : 4;
  return (
    <div style={{ marginBottom: 24 }}>
      <h3>{niveau.label}</h3>
      <p className="aide">
        Palier {niveau.code} — {decrireNiveau(niveau)}
        {phraseTempsMo(niveau.laborMinutes) !== null && <> · {phraseTempsMo(niveau.laborMinutes)}</>}
      </p>

      {niveau.lines.length === 0 ? (
        <div className="aide">Aucune ligne pour ce palier.</div>
      ) : (
        <div className="tableau-defilant">
          <table>
            <thead>
              <tr>
                <th>Désignation</th>
                <th style={{ width: 130 }}>Référence constructeur</th>
                <th style={{ width: 70 }}>Qté</th>
                {avecPrix && <th style={{ width: 90 }}>Prix du tarif</th>}
                {avecPrix && avecVente && <th style={{ width: 100 }}>Prix de vente</th>}
                {avecPrix && <th style={{ width: 100 }}>{avecVente ? 'Total vente' : 'Total'}</th>}
                {avecEquivalents && <th style={{ width: 130 }}>Référence adaptable</th>}
                {avecPrix && avecEquivalents && <th style={{ width: 90 }}>Prix adaptable</th>}
                {avecPrix && avecAchat && <th style={{ width: 100 }}>Coût d’achat</th>}
              </tr>
            </thead>
            <tbody>
              {niveau.lines.map((ligne) => (
                <tr key={ligne.lineKey}>
                  <td>
                    {ligne.designation}
                    {ligne.kind !== 'REPLACE' && (
                      <>
                        {' '}
                        <span className={`etat ${GENRE_LIGNE[ligne.kind].classe}`}>
                          {GENRE_LIGNE[ligne.kind].texte}
                        </span>
                      </>
                    )}
                    {ligne.note && <div className="aide">{ligne.note}</div>}
                  </td>
                  <td>{ligne.reference ? <code>{ligne.reference}</code> : '—'}</td>
                  <td title={ligne.quantity === null ? 'Quantité inconnue : à confirmer' : undefined}>
                    {quantite(ligne.quantity, ligne.unit)}
                  </td>
                  {avecPrix && <td>{prix(ligne.publicPrice)}</td>}
                  {avecPrix && avecVente && <CelluleVente ligne={ligne} />}
                  {avecPrix && <CelluleTotal ligne={ligne} avecVente={avecVente} />}
                  {avecEquivalents && (
                    <td>{ligne.adaptableReference ? <code>{ligne.adaptableReference}</code> : '—'}</td>
                  )}
                  {avecPrix && avecEquivalents && <td>{prix(ligne.internalPrice)}</td>}
                  {avecPrix && avecAchat && <CelluleAchat ligne={ligne} />}
                </tr>
              ))}
            </tbody>
            {sousTotal && (
              <tfoot>
                <tr className="sous-total-palier">
                  <td colSpan={decalageLibelle}>
                    Sous-total HT au prix {avecVente ? 'de vente' : 'du tarif'} — palier {niveau.code}
                  </td>
                  <td className={sousTotal.complet ? undefined : 'sous-total-incomplet'}>{montantSousTotal(sousTotal)}</td>
                  {nbColonnes - decalageLibelle - 1 > 0 && <td colSpan={nbColonnes - decalageLibelle - 1} />}
                </tr>
                {marge && (
                  <tr className="sous-total-palier">
                    <td colSpan={decalageLibelle}>Marge — palier {niveau.code}</td>
                    <td className={marge.complete ? undefined : 'sous-total-incomplet'}>{montantMarge(marge)}</td>
                    {nbColonnes - decalageLibelle - 1 > 0 && <td colSpan={nbColonnes - decalageLibelle - 1} />}
                  </tr>
                )}
                {reserve && (
                  <tr className="sous-total-reserve">
                    <td colSpan={nbColonnes}>{reserve}</td>
                  </tr>
                )}
                {reserveDeMarge && (
                  <tr className="sous-total-reserve">
                    <td colSpan={nbColonnes}>{reserveDeMarge}</td>
                  </tr>
                )}
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Le prix de vente d'une ligne : ce que voit le client. Une ligne sans prix de vente affiche « — » et dit pourquoi :
 * jamais un prix deviné.
 */
function CelluleVente({ ligne }: { ligne: GammeLigneVue }) {
  if (ligne.kind === 'CHECK') return <td>—</td>;
  const vente = ligne.salePrice;
  if (vente !== null && vente !== undefined) return <td>{prix(vente)}</td>;
  const raison =
    ligne.publicPrice === null || ligne.publicPrice === undefined
      ? 'Pas de prix du tarif : aucun prix de vente.'
      : 'Prix de vente non calculé : condition d’achat manquante pour cette référence ou sa famille.';
  return <td title={raison}>—</td>;
}

/** Le coût d'achat réel (celui de l'équivalent quand la ligne en a un). « — » quand il n'est pas calculé. */
function CelluleAchat({ ligne }: { ligne: GammeLigneVue }) {
  if (ligne.kind === 'CHECK') return <td>—</td>;
  const achat = ligne.purchasePrice;
  if (achat !== null && achat !== undefined) return <td>{prix(achat)}</td>;
  const raison =
    ligne.adaptableReference !== null
      ? 'Coût réel non calculé : l’équivalent n’a pas de prix, ou son fournisseur pas de condition d’achat.'
      : 'Coût d’achat non calculé : condition d’achat manquante.';
  return <td title={raison}>—</td>;
}

/**
 * Total d'une ligne de remplacement (quantité × prix de vente quand la version en porte, sinon × prix du tarif). Une
 * ligne qui ne compte pas dans le sous-total — contrôle, « si nécessaire », prix ou quantité absent — affiche « — »,
 * et dit pourquoi.
 */
function CelluleTotal({ ligne, avecVente }: { ligne: GammeLigneVue; avecVente: boolean }) {
  const centimes = totalLigneCentimes(ligne, avecVente ? 'VENTE' : 'TARIF');
  if (centimes !== null) return <td>{prix(centimes / 100)}</td>;
  const raison =
    ligne.kind === 'CONDITIONAL'
      ? 'Ligne « si nécessaire » : non comptée dans le sous-total.'
      : ligne.kind === 'REPLACE'
        ? 'Prix ou quantité absent : ligne non comptée dans le sous-total.'
        : undefined;
  return <td title={raison}>—</td>;
}
